import { useSupabaseClient } from '~/composables/useSupabaseClient'
import type { CheckFile } from '~/entities/checkFile'
import type { WorkItemSchedule } from '~/entities/workItemSchedule'
import { type Dayjs, formatDate } from '~/utils/date'

export const getNotStartedWorkItemSchedules = async (
  targetDate: Dayjs,
  checkFileId: CheckFile['id']
) => {
  // [NOTE] work_item_schedulesとcheck_sheet_assignmentsのジョイン時に、ジョイン先のcheck_sheet_assignmentsで
  // 絞り込みを行っても、Supabase側でクエリを最適化してくれていないためパフォーマンス劣化が見られる。
  // そのため事前にcheck_sheet_assignmentsを取得して、そのIDを使って絞り込む
  const { data: checkSheetAssignments, error: checkSheetAssignmentsError } =
    await useSupabaseClient()
      .from('check_sheet_assignments')
      .select('id')
      .eq('check_file_id', checkFileId)

  if (checkSheetAssignmentsError) throw checkSheetAssignmentsError

  if (checkSheetAssignments.length === 0) return []

  const { data, error } = await useSupabaseClient()
    .from('work_item_schedules')
    .select(
      '*, check_sheet_assignments!work_item_schedules_check_sheet_assignment_id_fkey!inner(*, check_files!inner(*, daily_verifiers(*, users!inner(*))), check_sheets!inner(*)), check_items!inner(*, frequency_settings!inner(*), timeboxes(*), number_conditions(*), images:example_images(*, images!inner(*)))'
    )
    .in(
      'check_sheet_assignment_id',
      checkSheetAssignments.map((checkSheetAssignment) => checkSheetAssignment.id)
    )
    // schedule_date <= targetDate <= end_date
    .lte('schedule_date', formatDate(targetDate)) // schedule_date <= targetDate
    .gte('end_date', formatDate(targetDate)) // end_date >= targetDate
    .eq('check_sheet_assignments.check_file_id', checkFileId)
    .eq('is_started', false)

  if (error) throw error
  return data
}

export const getWorkItemSchedulesByRange = async (props: {
  checkFileId: CheckFile['id']
  startDate: Dayjs
  endDate: Dayjs
  includeStarted: boolean
}) => {
  // [NOTE] work_item_schedulesとcheck_sheet_assignmentsのジョイン時に、ジョイン先のcheck_sheet_assignmentsで
  // 絞り込みを行っても、Supabase側でクエリを最適化してくれていないためパフォーマンス劣化が見られる。
  // そのため事前にcheck_sheet_assignmentsを取得して、そのIDを使って絞り込む
  const { data: checkSheetAssignments, error: checkSheetAssignmentsError } =
    await useSupabaseClient()
      .from('check_sheet_assignments')
      .select('id')
      .eq('check_file_id', props.checkFileId)

  if (checkSheetAssignmentsError) throw checkSheetAssignmentsError

  if (checkSheetAssignments.length === 0) return []

  const query = useSupabaseClient()
    .from('work_item_schedules')
    .select(
      '*, check_sheet_assignments!work_item_schedules_check_sheet_assignment_id_fkey!inner(*)'
    )
    .eq('check_sheet_assignments.check_file_id', props.checkFileId)
    .in(
      'check_sheet_assignment_id',
      checkSheetAssignments.map((checkSheetAssignment) => checkSheetAssignment.id)
    )
    .lte('schedule_date', formatDate(props.endDate))
    .gte('end_date', formatDate(props.startDate))
    .order('schedule_date', { ascending: true })

  if (!props.includeStarted) {
    query.eq('is_started', false)
  }

  const { data, error } = await query

  if (error) throw error
  return data
}

export const getCheckFileIdsWithWorkItemSchedulesOnDate = async (
  targetDate: Dayjs,
  checkFileIds: CheckFile['id'][]
): Promise<CheckFile['id'][]> => {
  if (checkFileIds.length === 0) return []

  const { data: checkSheetAssignments, error: checkSheetAssignmentsError } =
    await useSupabaseClient()
      .from('check_sheet_assignments')
      .select('id, check_file_id')
      .in('check_file_id', checkFileIds)

  if (checkSheetAssignmentsError) throw checkSheetAssignmentsError

  if (checkSheetAssignments.length === 0) return []

  const assignmentIdToCheckFileId = new Map(
    checkSheetAssignments.map((assignment) => [assignment.id, assignment.check_file_id])
  )

  const { data, error } = await useSupabaseClient()
    .from('work_item_schedules')
    .select('check_sheet_assignment_id')
    .in(
      'check_sheet_assignment_id',
      checkSheetAssignments.map((assignment) => assignment.id)
    )
    // schedule_date <= targetDate <= end_date
    .lte('schedule_date', formatDate(targetDate))
    .gte('end_date', formatDate(targetDate))
    .eq('is_started', false) // 未開始のものが対象

  if (error) throw error

  const result = new Set<CheckFile['id']>()

  data.forEach((item) => {
    const checkFileId = assignmentIdToCheckFileId.get(item.check_sheet_assignment_id)
    if (checkFileId) {
      result.add(checkFileId)
    }
  })

  return [...result]
}

export const updateWorkItemSchedules = async (
  ids: WorkItemSchedule['id'][],
  props: {
    isStarted?: boolean
    workStartedAt?: Dayjs | null
  }
) => {
  await useSupabaseClient()
    .from('work_item_schedules')
    .update({
      is_started: props.isStarted,
      work_started_at: props.workStartedAt === null ? null : props.workStartedAt?.toISOString(),
    })
    .in('id', ids)
}
